import { useState, useEffect, useRef, useCallback } from 'react';
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Inject,
  Sort,
  Filter,
  Page,
  Toolbar,
} from '@syncfusion/ej2-react-grids';
import { DialogComponent } from '@syncfusion/ej2-react-popups';
import { useTranslation } from 'react-i18next';
import { fetchClientErrors, type ClientError } from '../../services/ClientErrorService';

const severityColors: Record<string, string> = {
  CRITICAL: 'bg-red-600 text-white',
  ERROR: 'bg-red-500 text-white',
  HIGH: 'bg-orange-500 text-white',
  WARNING: 'bg-yellow-400 text-black',
  LOW: 'bg-blue-400 text-white',
  INFO: 'bg-gray-400 text-white',
};

const ClientErrorsPage = () => {
  const { t, i18n } = useTranslation(['common']);
  const [errors, setErrors] = useState<ClientError[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedError, setSelectedError] = useState<ClientError | null>(null);
  const [dialogVisible, setDialogVisible] = useState(false);
  const gridRef = useRef<GridComponent | null>(null);

  const loadErrors = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchClientErrors();
      setErrors(data);
    } catch (err) {
      console.error('Failed to fetch client errors', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadErrors();
  }, [loadErrors]);

  const formatTimestamp = (value: string | undefined) => {
    if (!value) return '';
    try {
      const date = new Date(value);
      return new Intl.DateTimeFormat(i18n.language === 'de' ? 'de-DE' : 'en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }).format(date);
    } catch {
      return value;
    }
  };

  const severityTemplate = (data: ClientError) => {
    const colorClass = severityColors[data.severity] || 'bg-gray-300 text-black';
    return (
      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${colorClass}`}>
        {data.severity}
      </span>
    );
  };

  const timestampTemplate = (data: ClientError) => {
    return <span>{formatTimestamp(data.timestamp)}</span>;
  };

  const errorCountTemplate = (data: ClientError) => {
    if (data.errorCount > 1) {
      return <span className="font-semibold text-red-600">{data.errorCount}</span>;
    }
    return <span>{data.errorCount}</span>;
  };

  const resolvedTemplate = (data: ClientError) => {
    return data.resolved
      ? <span className="text-green-600 font-semibold">{t('clientErrors.resolved')}</span>
      : <span className="text-gray-400">{t('clientErrors.unresolved')}</span>;
  };

  const onRowSelected = () => {
    if (gridRef.current) {
      const selected = gridRef.current.getSelectedRecords() as ClientError[];
      if (selected.length > 0) {
        setSelectedError(selected[0]);
        setDialogVisible(true);
      }
    }
  };

  const dialogButtons = [
    {
      click: () => setDialogVisible(false),
      buttonModel: { content: t('common.close', 'Close'), isPrimary: true },
    },
  ];

  const toolbarItems = [
    { text: t('clientErrors.refresh', 'Refresh'), prefixIcon: 'e-icons e-refresh', id: 'refresh' },
  ];

  const toolbarClick = (args: { item?: { id?: string } }) => {
    if (args.item?.id?.endsWith('refresh')) {
      loadErrors();
    }
  };

  return (
    <div className="p-6">
      <h4 className="text-xl font-bold mb-4">
        {t('clientErrors.title', 'Client Errors')}
      </h4>

      {loading ? (
        <div className="flex items-center justify-center py-10">
          <span className="text-gray-500">{t('common.loading', 'Loading...')}</span>
        </div>
      ) : (
        <GridComponent
          ref={gridRef}
          locale="de-DE"
          dataSource={errors}
          allowPaging={true}
          allowSorting={true}
          allowFiltering={true}
          pageSettings={{ pageSize: 20 }}
          filterSettings={{ type: 'Excel' }}
          toolbar={toolbarItems}
          toolbarClick={toolbarClick}
          rowSelected={onRowSelected}
          enableHover={true}
          height="calc(100vh - 240px)"
        >
          <ColumnsDirective>
            <ColumnDirective
              field="timestamp"
              headerText={t('clientErrors.grid.timestamp', 'Timestamp')}
              width="160"
              template={timestampTemplate}
              type="date"
            />
            <ColumnDirective
              field="severity"
              headerText={t('clientErrors.grid.severity', 'Severity')}
              width="100"
              template={severityTemplate}
            />
            <ColumnDirective
              field="category"
              headerText={t('clientErrors.grid.category', 'Category')}
              width="130"
            />
            <ColumnDirective
              field="message"
              headerText={t('clientErrors.grid.message', 'Message')}
              width="300"
            />
            <ColumnDirective
              field="username"
              headerText={t('clientErrors.grid.username', 'User')}
              width="120"
            />
            <ColumnDirective
              field="browser"
              headerText={t('clientErrors.grid.browser', 'Browser')}
              width="140"
            />
            <ColumnDirective
              field="errorCount"
              headerText={t('clientErrors.grid.errorCount', 'Count')}
              width="80"
              textAlign="Right"
              template={errorCountTemplate}
            />
            <ColumnDirective
              field="url"
              headerText={t('clientErrors.grid.url', 'URL')}
              width="200"
            />
            <ColumnDirective
              field="resolved"
              headerText={t('clientErrors.grid.resolved', 'Status')}
              width="100"
              template={resolvedTemplate}
            />
          </ColumnsDirective>
          <Inject services={[Sort, Filter, Page, Toolbar]} />
        </GridComponent>
      )}

      {dialogVisible && selectedError && (
      <DialogComponent
        visible={true}
        width="700px"
        header={t('clientErrors.detailTitle', 'Error Details')}
        showCloseIcon={true}
        close={() => setDialogVisible(false)}
        buttons={dialogButtons}
        isModal={true}
        animationSettings={{ effect: 'Zoom' }}
      >
        <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <span className="font-semibold">{t('clientErrors.detail.severity', 'Severity')}:</span>{' '}
                {severityTemplate(selectedError)}
              </div>
              <div>
                <span className="font-semibold">{t('clientErrors.detail.category', 'Category')}:</span>{' '}
                {selectedError.category}
              </div>
              <div>
                <span className="font-semibold">{t('clientErrors.detail.timestamp', 'Timestamp')}:</span>{' '}
                {formatTimestamp(selectedError.timestamp)}
              </div>
              <div>
                <span className="font-semibold">{t('clientErrors.detail.errorCount', 'Occurrences')}:</span>{' '}
                {selectedError.errorCount}
              </div>
              <div>
                <span className="font-semibold">{t('clientErrors.detail.firstOccurrence', 'First seen')}:</span>{' '}
                {formatTimestamp(selectedError.firstOccurrence)}
              </div>
              <div>
                <span className="font-semibold">{t('clientErrors.detail.lastOccurrence', 'Last seen')}:</span>{' '}
                {formatTimestamp(selectedError.lastOccurrence)}
              </div>
              <div>
                <span className="font-semibold">{t('clientErrors.detail.username', 'User')}:</span>{' '}
                {selectedError.username || '-'}
              </div>
              <div>
                <span className="font-semibold">{t('clientErrors.detail.tenant', 'Tenant')}:</span>{' '}
                {selectedError.tenantName || '-'}
              </div>
              <div>
                <span className="font-semibold">{t('clientErrors.detail.browser', 'Browser')}:</span>{' '}
                {selectedError.browserName} {selectedError.browserVersion}
              </div>
              <div>
                <span className="font-semibold">{t('clientErrors.detail.os', 'OS')}:</span>{' '}
                {selectedError.os || '-'}
              </div>
              <div>
                <span className="font-semibold">{t('clientErrors.detail.screenResolution', 'Screen')}:</span>{' '}
                {selectedError.screenResolution || '-'}
              </div>
              <div>
                <span className="font-semibold">{t('clientErrors.detail.component', 'Component')}:</span>{' '}
                {selectedError.component || '-'}
              </div>
            </div>
            {selectedError.url && (
              <div>
                <span className="font-semibold">{t('clientErrors.detail.url', 'URL')}:</span>{' '}
                <span className="break-all">{selectedError.url}</span>
              </div>
            )}
            <div>
              <span className="font-semibold">{t('clientErrors.detail.message', 'Message')}:</span>
              <div className="mt-1 p-2 bg-gray-100 rounded whitespace-pre-wrap break-words">
                {selectedError.message}
              </div>
            </div>
            {selectedError.stackTrace && (
              <div>
                <span className="font-semibold">{t('clientErrors.detail.stackTrace', 'Stack Trace')}:</span>
                <pre className="mt-1 p-2 bg-gray-900 text-green-400 rounded text-xs overflow-auto max-h-64 whitespace-pre-wrap break-words">
                  {selectedError.stackTrace}
                </pre>
              </div>
            )}
            {selectedError.resolved && (
              <div className="border-t pt-2 mt-2">
                <span className="font-semibold text-green-600">{t('clientErrors.detail.resolvedBy', 'Resolved by')}:</span>{' '}
                {selectedError.resolvedBy} ({formatTimestamp(selectedError.resolvedOn)})
              </div>
            )}
          </div>
      </DialogComponent>
      )}
    </div>
  );
};

export default ClientErrorsPage;
